/**
 * useRoving: the one keyboard model for a group of radios or tabs (WAI-ARIA roving tabindex). Put `ref` and `onKeyDown` on the
 * `role="radiogroup"` / `role="tablist"` element: the selected item is the one tab stop (the first enabled one when none is),
 * the arrow keys, Home and End move focus, and with `follow` (the default) the focused item is also chosen, as a radio or an
 * automatic tab is. A group whose items only open and close a panel passes `follow: false`.
 */
import { useLayoutEffect, useRef, type ComponentPropsWithoutRef, type KeyboardEvent, type RefObject } from "react";

const ITEM = "[role='radio'], [role='tab']";
const NEXT = new Set(["ArrowRight", "ArrowDown"]);
const PREV = new Set(["ArrowLeft", "ArrowUp"]);

const itemsOf = (group: HTMLElement | null): HTMLElement[] =>
  group === null ? [] : [...group.querySelectorAll<HTMLElement>(ITEM)].filter((el) => el.closest("[role='radiogroup'], [role='tablist']") === group && el.getAttribute("aria-disabled") !== "true" && !el.hasAttribute("disabled"));

const isOn = (el: HTMLElement) => el.getAttribute("aria-checked") === "true" || el.getAttribute("aria-selected") === "true";

export function useRoving<T extends HTMLElement = HTMLDivElement>({ follow = true }: { readonly follow?: boolean } = {}): {
  readonly ref: RefObject<T | null>; readonly onKeyDown: (e: KeyboardEvent<T>) => void;
} {
  const ref = useRef<T>(null);
  // After every render: one tab stop. Disabled items stay out of the order.
  useLayoutEffect(() => {
    const group = ref.current;
    if (group === null) return;
    const live = itemsOf(group);
    const stop = live.find(isOn) ?? live[0];
    for (const el of group.querySelectorAll<HTMLElement>(ITEM)) if (el.closest("[role='radiogroup'], [role='tablist']") === group) el.tabIndex = el === stop ? 0 : -1;
  });
  const onKeyDown = (e: KeyboardEvent<T>) => {
    const live = itemsOf(ref.current);
    const at = live.indexOf(e.target as HTMLElement);
    if (at < 0 || live.length === 0) return;
    let to: number | null = null;
    if (NEXT.has(e.key)) to = (at + 1) % live.length;
    else if (PREV.has(e.key)) to = (at - 1 + live.length) % live.length;
    else if (e.key === "Home") to = 0;
    else if (e.key === "End") to = live.length - 1;
    const target = to === null ? undefined : live[to];
    if (target === undefined) return;
    e.preventDefault();
    target.focus();
    if (follow && !isOn(target)) target.click();
  };
  return { ref, onKeyDown };
}

/** A `div` that carries the keyboard model: `<RovingGroup role="radiogroup" aria-label="…">` or `role="tablist"`. */
export function RovingGroup({ follow, ...rest }: ComponentPropsWithoutRef<"div"> & { readonly follow?: boolean }) {
  const roving = useRoving(follow === undefined ? {} : { follow });
  return <div {...rest} ref={roving.ref} onKeyDown={roving.onKeyDown} />;
}
